"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.newPreviousPeriodMeasure = exports.newPopMeasure = exports.newArithmeticMeasure = exports.modifyPreviousPeriodMeasure = exports.modifyPopMeasure = exports.modifySimpleMeasure = exports.modifyMeasure = exports.newMeasure = exports.PreviousPeriodMeasureBuilder = exports.PoPMeasureBuilder = exports.ArithmeticMeasureBuilder = exports.MeasureBuilder = exports.MeasureBuilderBase = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var identity_1 = tslib_1.__importDefault(require("lodash/identity"));
var index_1 = require("./index");
var objRef_1 = require("../../objRef");
var factory_1 = require("../../objRef/factory");
var spark_md5_1 = tslib_1.__importDefault(require("spark-md5"));
var ts_invariant_1 = tslib_1.__importStar(require("ts-invariant"));
var sanitizeLocalId_1 = require("../../sanitizeLocalId");
/**
 * Abstract base class for measure builders. Measure builders allow for incremental, fluent construction
 * (and optionally modification) of measures.
 *
 * You should not be instantiating the builders directly. Instead, rely on the different functions to
 * create different types of measures.
 *
 * @public
 */
var MeasureBuilderBase = /** @class */ (function () {
    /**
     * @internal
     */
    function MeasureBuilderBase() {
        var _this = this;
        this.customLocalId = false;
        /**
         * Sets local identifier (localId) for the measure. LocalId can be used to reference the measure
         * within the execution definition.
         *
         * Normally, builder will generate localId based on contents of the measure definition - taking all
         * properties into account: in typical scenarios you don't have to call this function at all. The only exception
         * where you have to provide custom local id is if your execution must contain the exact same measure twice.
         *
         * For convenience, this method also accepts 'undefined', which indicates that the default local id generation
         * logic should be used.
         *
         * @param localId - local identifier to set; if not specified, the builder will ensure local id will
         * be generated
         */
        this.localId = function (localId) {
            if (!localId || localId.trim().length === 0) {
                return _this.defaultLocalId();
            }
            _this.measure.localIdentifier = localId;
            _this.customLocalId = true;
            return _this;
        };
        /**
         * Indicates that the measure's localId should be generated using the default local-id generator logic.
         */
        this.defaultLocalId = function () {
            _this.measure.localIdentifier = "";
            _this.customLocalId = false;
            return _this;
        };
        /**
         * Sets alias - alternative title - for the measure. This value will then be used in various
         * chart-specific descriptive elements. For convenience if no alias is specified, the measure
         * will fall back to using either title (if specified) or server-defined title as the ultimate fallback
         *
         * @param alias - alias to use instead of measure title; undefined to use the title instead
         */
        this.alias = function (alias) {
            if (!alias) {
                return _this.noAlias();
            }
            _this.measure.alias = alias;
            return _this;
        };
        /**
         * Resets alias - alternative title - set for the measure. The measure title will be used if specified, otherwise
         * the server-defined title will be used instead.
         */
        this.noAlias = function () {
            delete _this.measure.alias;
            return _this;
        };
        /**
         * Sets alternative title for the measure. This value will then be used in various chart-specific
         * descriptive elements. For convenience if no title is specified, the measure will fall back to server-defined
         * value.
         *
         * @param title - alternative title to use instead of server-defined value; undefined to use server-defined value
         */
        this.title = function (title) {
            if (!title) {
                return _this.noTitle();
            }
            _this.measure.title = title;
            return _this;
        };
        /**
         * Resets alternative title for the measure. The server-defined title of the measure will be used
         * instead.
         */
        this.noTitle = function () {
            delete _this.measure.title;
            return _this;
        };
        /**
         * Sets measure format to use when rendering values calculated from this measure. The format string
         * is described in more detail here {@link https://help.gooddata.com/doc/en/reporting-and-dashboards/reports/working-with-reports/formatting-numbers-in-reports}.
         *
         * For convenience, if you do not specify any format, then a default server-defined value will be used instead.
         *
         * @param format - measure format string; or undefined if you want to fall back to server-defined value
         */
        this.format = function (format) {
            if (!format) {
                return _this.defaultFormat();
            }
            _this.measure.format = format;
            return _this;
        };
        /**
         * Resets format string to the server-defined value.
         */
        this.defaultFormat = function () {
            delete _this.measure.format;
            return _this;
        };
        this.build = function () {
            var envelope = _this.buildEnvelope();
            return {
                measure: tslib_1.__assign(tslib_1.__assign({}, envelope), { definition: _this.buildDefinition() }),
            };
        };
        this.measure = { localIdentifier: "" };
    }
    /**
     * The measure builder subclasses must call this when they are used to modify
     * an existing measure. Existing measure modification returns a new object that
     * reflects the mods however it keeps the localId set as-is.
     *
     * @param measure - envelope of measure being modified
     */
    MeasureBuilderBase.prototype.initializeFromExisting = function (measure) {
        this.measure = cloneDeep_1.default(measure);
        this.measure.localIdentifier = measure.localIdentifier;
        this.customLocalId = true;
    };
    /**
     * If custom localId has been set using localId() function, then use it unless it is empty.
     *
     * In all other cases generate localId. The localId generation consists up from three parts:
     *
     * - local identifier always starts with letter 'm'
     * - IF alias, title or format is specified, it is hashed and first 8 chars of the hash will follow
     * - The measure type specific part of the local identifier follows
     *
     * These three parts are separated using underscore.
     */
    MeasureBuilderBase.prototype.getOrGenerateLocalId = function () {
        if (this.customLocalId && !isEmpty_1.default(this.measure.localIdentifier)) {
            return this.measure.localIdentifier;
        }
        return sanitizeLocalId_1.sanitizeLocalId(["m", this.buildEnvelopeLocalIdPart(), this.generateLocalId()]
            .filter(function (part) { return !isEmpty_1.default(part); })
            .join("_"));
    };
    MeasureBuilderBase.prototype.buildEnvelopeLocalIdPart = function () {
        var _a = this.measure, alias = _a.alias, format = _a.format, title = _a.title;
        if (!alias && !format && !title) {
            return "";
        }
        var hasher = new spark_md5_1.default();
        hasher.append("alias_" + (alias !== null && alias !== void 0 ? alias : ""));
        hasher.append("format_" + (format !== null && format !== void 0 ? format : ""));
        hasher.append("title_" + (title !== null && title !== void 0 ? title : ""));
        return hasher.end().substr(0, 8);
    };
    MeasureBuilderBase.prototype.buildEnvelope = function () {
        return tslib_1.__assign(tslib_1.__assign({}, this.measure), { localIdentifier: this.getOrGenerateLocalId() });
    };
    return MeasureBuilderBase;
}());
exports.MeasureBuilderBase = MeasureBuilderBase;
/**
 * Builder for simple measures.
 *
 * Do not instantiate this builder directly, instead use {@link newMeasure} or {@link modifyMeasure} functions.
 *
 * @public
 */
var MeasureBuilder = /** @class */ (function (_super) {
    tslib_1.__extends(MeasureBuilder, _super);
    /**
     * @internal
     */
    function MeasureBuilder(measureOrRef) {
        var _this = _super.call(this) || this;
        /**
         * Sets aggregation to use for measures created from facts. By default the aggregation is SUM. For convenience
         * the aggregation can be specified also for measures created from metrics - and in that case it will be ignored.
         *
         * For convenience, the aggregation may be undefined and it means the value should be reset to the default.
         *
         * @param aggregation - aggregation to use; if undefined will reset to default
         */
        _this.aggregation = function (aggregation) {
            if (!aggregation) {
                return _this.defaultAggregation();
            }
            _this.measureDefinition.aggregation = aggregation;
            return _this;
        };
        /**
         * Resets measure aggregation to the default (SUM).
         */
        _this.defaultAggregation = function () {
            delete _this.measureDefinition.aggregation;
            return _this;
        };
        /**
         * Indicates that the measure values should be calculated as percent contributions to the total unsliced
         * value.
         *
         * This method works as 'turn-on-toggle' by default, however you can specify the actual boolean parameter and
         * turn the ratio computation off using this method.
         *
         * @param value - set the compute ratio indicator to this value
         */
        _this.ratio = function (value) {
            if (value === void 0) { value = true; }
            if (!value) {
                return _this.noRatio();
            }
            _this.measureDefinition.computeRatio = true;
            return _this;
        };
        /**
         * Resets compute as ratio indicator.
         */
        _this.noRatio = function () {
            delete _this.measureDefinition.computeRatio;
            return _this;
        };
        /**
         * Sets filters to apply when calculating the values of this measure. These filters apply only to this particular
         * measure calculation and do not impact the rest of the execution.
         *
         * @param filters - filters to apply to this measure
         */
        _this.filters = function () {
            var filters = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                filters[_i] = arguments[_i];
            }
            _this.measureDefinition.filters = filters;
            return _this;
        };
        /**
         * Resets measure filters - this will remove all filters from the measure.
         */
        _this.noFilters = function () {
            _this.measureDefinition.filters = [];
            return _this;
        };
        /**
         * Sets reference to measure item that will be used for calculation. This can be either reference to
         * a MAQL metric or a fact to calculate from.
         *
         * @param ref - new reference to use
         */
        _this.measureItem = function (ref) {
            _this.measureDefinition.item = ref;
            return _this;
        };
        if (index_1.isMeasure(measureOrRef)) {
            _this.initializeFromExisting(measureOrRef.measure);
            _this.measureDefinition = cloneDeep_1.default(measureOrRef.measure.definition.measureDefinition);
        }
        else {
            _this.measureDefinition = {
                item: measureOrRef,
            };
        }
        return _this;
    }
    MeasureBuilder.prototype.generateLocalId = function () {
        var aggString = this.measureDefinition.aggregation ? "_" + this.measureDefinition.aggregation : "";
        var ratioString = this.measureDefinition.computeRatio ? "_ratio" : "";
        return "" + objRef_1.objRefToString(this.measureDefinition.item) + aggString + ratioString + this.filterLocalIdString();
    };
    MeasureBuilder.prototype.buildDefinition = function () {
        return {
            measureDefinition: this.measureDefinition,
        };
    };
    MeasureBuilder.prototype.filterLocalIdString = function () {
        if (isEmpty_1.default(this.measureDefinition.filters)) {
            return "";
        }
        var hasher = new spark_md5_1.default();
        hasher.append(JSON.stringify(this.measureDefinition.filters));
        return "_" + hasher.end().substr(0, 8);
    };
    return MeasureBuilder;
}(MeasureBuilderBase));
exports.MeasureBuilder = MeasureBuilder;
/**
 * Builder for arithmetic measures.
 *
 * Do not instantiate this builder directly, instead use {@link newArithmeticMeasure}.
 *
 * @public
 */
var ArithmeticMeasureBuilder = /** @class */ (function (_super) {
    tslib_1.__extends(ArithmeticMeasureBuilder, _super);
    /**
     * @internal
     */
    function ArithmeticMeasureBuilder(input) {
        var _this = _super.call(this) || this;
        /**
         * Sets arithmetic operator to apply when calculating the arithmetic measure.
         *
         * @param op - operator
         */
        _this.operator = function (op) {
            _this.arithmeticMeasure.operator = op;
            return _this;
        };
        /**
         * Sets operands for arithmetic: other measures specified by either value or local identifier -
         *
         * @param measuresOrLocalIds - array of measures and/or localIds of measures to use as operands
         */
        _this.operands = function (measuresOrLocalIds) {
            _this.arithmeticMeasure.measureIdentifiers = measuresOrLocalIds.map(index_1.measureLocalId);
            return _this;
        };
        if (index_1.isArithmeticMeasure(input)) {
            _this.initializeFromExisting(input.measure);
            _this.arithmeticMeasure = cloneDeep_1.default(input.measure.definition.arithmeticMeasure);
        }
        else {
            var measureIdentifiers = input.measuresOrIds.map(index_1.measureLocalId);
            _this.arithmeticMeasure = {
                measureIdentifiers: measureIdentifiers,
                operator: input.operator,
            };
        }
        return _this;
    }
    ArithmeticMeasureBuilder.prototype.buildDefinition = function () {
        return {
            arithmeticMeasure: this.arithmeticMeasure,
        };
    };
    ArithmeticMeasureBuilder.prototype.generateLocalId = function () {
        var hasher = new spark_md5_1.default();
        this.arithmeticMeasure.measureIdentifiers.forEach(function (id) { return hasher.append(id); });
        return hasher.end();
    };
    return ArithmeticMeasureBuilder;
}(MeasureBuilderBase));
exports.ArithmeticMeasureBuilder = ArithmeticMeasureBuilder;
/**
 * Builder for period-over-period measures.
 *
 * Do not instantiate this builder directly, instead use {@link newPopMeasure}.
 *
 * @public
 */
var PoPMeasureBuilder = /** @class */ (function (_super) {
    tslib_1.__extends(PoPMeasureBuilder, _super);
    /**
     * @internal
     */
    function PoPMeasureBuilder(input) {
        var _this = _super.call(this) || this;
        /**
         * Sets master measure from which this period-over-period measure should be calculated.
         *
         * @param measureOrLocalId - measure value or measure local identifier
         */
        _this.masterMeasure = function (measureOrLocalId) {
            _this.popMeasureDefinition.measureIdentifier = index_1.measureLocalId(measureOrLocalId);
            return _this;
        };
        /**
         * Sets period-over-period date dimension attribute to use for offsetting. For convenience the attribute
         * may be specified by either object reference or as a string - in which case it is assumed this is identifier
         * of the attribute object.
         *
         * @param popAttrIdOrRef - reference of the PoP attribute, or identifier
         */
        _this.popAttribute = function (popAttrIdOrRef) {
            _this.popMeasureDefinition.popAttribute = objRef_1.isObjRef(popAttrIdOrRef)
                ? popAttrIdOrRef
                : factory_1.idRef(popAttrIdOrRef, "attribute");
            return _this;
        };
        if (index_1.isPoPMeasure(input)) {
            _this.initializeFromExisting(input.measure);
            _this.popMeasureDefinition = cloneDeep_1.default(input.measure.definition.popMeasureDefinition);
        }
        else {
            var measureIdentifier = index_1.measureLocalId(input.measureOrLocalId);
            var popAttribute = objRef_1.isObjRef(input.popAttrIdOrRef)
                ? input.popAttrIdOrRef
                : factory_1.idRef(input.popAttrIdOrRef, "attribute");
            _this.popMeasureDefinition = {
                measureIdentifier: measureIdentifier,
                popAttribute: popAttribute,
            };
        }
        return _this;
    }
    PoPMeasureBuilder.prototype.buildDefinition = function () {
        return {
            popMeasureDefinition: this.popMeasureDefinition,
        };
    };
    PoPMeasureBuilder.prototype.generateLocalId = function () {
        return this.popMeasureDefinition.measureIdentifier + "_" + objRef_1.objRefToString(this.popMeasureDefinition.popAttribute);
    };
    return PoPMeasureBuilder;
}(MeasureBuilderBase));
exports.PoPMeasureBuilder = PoPMeasureBuilder;
/**
 * Builder for previous period measures.
 *
 * Do not instantiate this builder directly, instead use {@link newPreviousPeriodMeasure}.
 *
 * @public
 */
var PreviousPeriodMeasureBuilder = /** @class */ (function (_super) {
    tslib_1.__extends(PreviousPeriodMeasureBuilder, _super);
    /**
     * @internal
     */
    function PreviousPeriodMeasureBuilder(input) {
        var _this = _super.call(this) || this;
        /**
         * Sets master measure from which this previous period measure should be calculated.
         *
         * @param measureOrLocalId - measure value or measure local identifier
         */
        _this.masterMeasure = function (measureOrLocalId) {
            _this.previousPeriodMeasure.measureIdentifier = index_1.measureLocalId(measureOrLocalId);
            return _this;
        };
        /**
         * Sets date data set + offset within the data set to use when calculating values of this measure.
         *
         * @param dd - date data set + offset
         */
        _this.dateDataSets = function (dd) {
            _this.previousPeriodMeasure.dateDataSets = _this.convertDd(dd);
            return _this;
        };
        _this.convertDd = function (dd) {
            return dd.map(function (d) { return (tslib_1.__assign(tslib_1.__assign({}, d), { dataSet: typeof d.dataSet === "string" ? factory_1.idRef(d.dataSet) : d.dataSet })); });
        };
        if (index_1.isPreviousPeriodMeasure(input)) {
            _this.initializeFromExisting(input.measure);
            _this.previousPeriodMeasure = cloneDeep_1.default(input.measure.definition.previousPeriodMeasure);
        }
        else {
            _this.previousPeriodMeasure = {
                measureIdentifier: index_1.measureLocalId(input.measureIdOrLocalId),
                dateDataSets: _this.convertDd(input.dateDataSets),
            };
        }
        return _this;
    }
    PreviousPeriodMeasureBuilder.prototype.buildDefinition = function () {
        return {
            previousPeriodMeasure: this.previousPeriodMeasure,
        };
    };
    PreviousPeriodMeasureBuilder.prototype.generateLocalId = function () {
        return this.previousPeriodMeasure.measureIdentifier + "_previous_period";
    };
    return PreviousPeriodMeasureBuilder;
}(MeasureBuilderBase));
exports.PreviousPeriodMeasureBuilder = PreviousPeriodMeasureBuilder;
/**
 * Creates a new measure with the specified identifier and optional modifications and localIdentifier.
 * @param measure - ref of identifier of the measure
 * @param modifications - optional modifications (e.g. alias, title, etc.)
 * @public
 */
function newMeasure(measure, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    var ref = objRef_1.isObjRef(measure) ? measure : factory_1.idRef(measure);
    var builder = new MeasureBuilder(ref);
    return modifications(builder).build();
}
exports.newMeasure = newMeasure;
/**
 * Creates a new measure by applying modifications on top of an existing measure. This generic function can
 * accept measure of any type and thus in returns allows modifications on the properties that are common
 * in any type of measure.
 *
 * This operation is immutable and will not alter the input measure.
 *
 * The returned measure will have the same localIdentifier as the original measure. If you would like to assign
 * new/different local identifier to the measure, you can do that using the modifications where you can provide
 * either new custom localId or indicate that the measure should fall back to the auto-generated localId.
 *
 * @param measure - measure to use as template for the new measure
 * @param modifications - modifications to apply
 * @returns new instance
 * @public
 */
function modifyMeasure(measure, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    ts_invariant_1.default(measure, "measure must be specified");
    var builder = createBuilder(measure);
    return modifications(builder).build();
}
exports.modifyMeasure = modifyMeasure;
function createBuilder(measure) {
    if (index_1.isSimpleMeasure(measure)) {
        return new MeasureBuilder(measure);
    }
    else if (index_1.isArithmeticMeasure(measure)) {
        return new ArithmeticMeasureBuilder(measure);
    }
    else if (index_1.isPoPMeasure(measure)) {
        return new PoPMeasureBuilder(measure);
    }
    else if (index_1.isPreviousPeriodMeasure(measure)) {
        return new PreviousPeriodMeasureBuilder(measure);
    }
    throw new ts_invariant_1.InvariantError("unexpected measure type");
}
/**
 * Creates a new simple measure by applying modifications on top of an existing measure.
 *
 * This operation is immutable and will not alter the input measure.
 *
 * The returned measure will have the same localIdentifier as the original measure. If you would like to assign
 * new/different local identifier to the measure, you can do that using the modifications where you can provide
 * either new custom localId or indicate that the measure should fall back to the auto-generated localId.
 *
 * @param measure - measure to use as template for the new measure
 * @param modifications - modifications to apply
 * @returns new instance
 * @public
 */
function modifySimpleMeasure(measure, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    ts_invariant_1.default(measure, "measure must be specified");
    var builder = new MeasureBuilder(measure);
    return modifications(builder).build();
}
exports.modifySimpleMeasure = modifySimpleMeasure;
/**
 * Creates a new PoP measure by applying modifications on top of an existing measure.
 *
 * This operation is immutable and will not alter the input measure.
 *
 * The returned measure will have the same localIdentifier as the original measure. If you would like to assign
 * new/different local identifier to the measure, you can do that using the modifications where you can provide
 * either new custom localId or indicate that the measure should fall back to the auto-generated localId.
 *
 * @param measure - measure to use as template for the new measure
 * @param modifications - modifications to apply
 * @returns new instance
 * @public
 */
function modifyPopMeasure(measure, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    ts_invariant_1.default(measure, "measure must be specified");
    var builder = new PoPMeasureBuilder(measure);
    return modifications(builder).build();
}
exports.modifyPopMeasure = modifyPopMeasure;
/**
 * Creates a new Previous Period measure by applying modifications on top of an existing measure.
 *
 * This operation is immutable and will not alter the input measure.
 *
 * The returned measure will have the same localIdentifier as the original measure. If you would like to assign
 * new/different local identifier to the measure, you can do that using the modifications where you can provide
 * either new custom localId or indicate that the measure should fall back to the auto-generated localId.
 *
 * @param measure - measure to use as template for the new measure
 * @param modifications - modifications to apply
 * @returns new instance
 * @public
 */
function modifyPreviousPeriodMeasure(measure, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    ts_invariant_1.default(measure, "measure must be specified");
    var builder = new PreviousPeriodMeasureBuilder(measure);
    return modifications(builder).build();
}
exports.modifyPreviousPeriodMeasure = modifyPreviousPeriodMeasure;
/**
 * Creates a new arithmetic measure with the specified measure identifiers and operator and optional modifications and localIdentifier.
 * @param measuresOrIds - measures or identifiers of the measures to be included in this arithmetic measure
 * @param operator - operator of the measure
 * @param modifications - optional modifications (e.g. alias, title, etc.)
 * @public
 */
function newArithmeticMeasure(measuresOrIds, operator, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    var builder = new ArithmeticMeasureBuilder({ measuresOrIds: measuresOrIds, operator: operator });
    return modifications(builder).build();
}
exports.newArithmeticMeasure = newArithmeticMeasure;
/**
 * Creates a new PoP measure with the specified identifier and PoP attribute identifier and optional modifications and localIdentifier.
 * @param measureOrLocalId - measure or local identifier of the measure
 * @param popAttrIdOrRef - identifier or a reference to PoP attribute
 * @param modifications - optional modifications (e.g. alias, title, etc.)
 * @public
 */
function newPopMeasure(measureOrLocalId, popAttrIdOrRef, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    var builder = new PoPMeasureBuilder({ measureOrLocalId: measureOrLocalId, popAttrIdOrRef: popAttrIdOrRef });
    return modifications(builder).build();
}
exports.newPopMeasure = newPopMeasure;
/**
 * Creates a new Previous Period measure with the specified measure identifier and date data sets and optional modifications and localIdentifier.
 * @param measureIdOrLocalId - measure or local identifier of the measure to create Previous Period measure for
 * @param dateDataSets - date data sets to use in the Previous Period calculation
 * @param modifications - optional modifications (e.g. alias, title, etc.)
 * @public
 */
function newPreviousPeriodMeasure(measureIdOrLocalId, dateDataSets, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    var builder = new PreviousPeriodMeasureBuilder({ measureIdOrLocalId: measureIdOrLocalId, dateDataSets: dateDataSets });
    return modifications(builder).build();
}
exports.newPreviousPeriodMeasure = newPreviousPeriodMeasure;
//# sourceMappingURL=factory.js.map